import React, { useState } from "react";
import { IconButton, InputAdornment } from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";

import { useDBTInterruptActions } from "../../../../../store/useDBTInterruptStore";
import {
  ButtonsContainer,
  CancelButton,
  FieldsContainer,
  FormContainer,
  FormTitle,
  StyledCard,
  StyledCardContent,
  StyledTextField,
  SubmitButton,
} from "./style";
import useChatStream from "../../../ChatBot/hooks/useChatStream";
import {
  useSessionActions,
  useSessionStore,
} from "../../../../../store/useSessionStore";
import { useParams } from "react-router-dom";
import chatApi from "../../../../../api/chatApi";

type Props = {};

interface FormData {
  github_dbt_folder_link: string;
  github_pat: string;
  data_limit: number;
}

interface FormErrors {
  github_dbt_folder_link?: string;
  github_pat?: string;
  data_limit?: string;
}

const GithubConfigForm = (props: Props) => {
  const { threadId } = useParams();
  const activeRunId = useSessionStore.use.activeRunId();
  const thread = useSessionStore.use.thread();
  const interruptAction = useSessionStore.use.interruptAction();

  const { streamHITLHandler } = useChatStream();
  const { setActiveRunId, setStream, setInterruptAction, clearSteps } =
    useSessionActions();
  const { setDBTGithubConfigValues } = useDBTInterruptActions();

  // Local form state
  const [formData, setFormData] = useState<FormData>({
    github_dbt_folder_link: "",
    github_pat: "",
    data_limit: 5,
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Validation functions
  const validateGithubUrl = (url: string): string | undefined => {
    if (!url.trim()) {
      return "GitHub DBT URL is required";
    }

    const githubUrlPattern = /^https:\/\/github\.com\/[\w\-\.]+\/[\w\-\.]+/;
    if (!githubUrlPattern.test(url)) {
      return "Please enter a valid GitHub repository URL";
    }

    return undefined;
  };

  const validatePAT = (pat: string): string | undefined => {
    if (!pat.trim()) {
      return "GitHub Personal Access Token is required";
    }

    if (pat.length < 40) {
      return "GitHub Personal Access Token must be at least 40 characters";
    }

    return undefined;
  };

  const validateDataLimit = (limit: number): string | undefined => {
    if (!limit || limit < 1) {
      return "Data limit must be a positive number";
    }

    if (limit > 10000) {
      return "Data limit cannot exceed 10,000";
    }

    return undefined;
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    const urlError = validateGithubUrl(formData.github_dbt_folder_link);
    if (urlError) newErrors.github_dbt_folder_link = urlError;

    const patError = validatePAT(formData.github_pat);
    if (patError) newErrors.github_pat = patError;

    const dataLimitError = validateDataLimit(formData.data_limit);
    if (dataLimitError) newErrors.data_limit = dataLimitError;

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange =
    (field: keyof FormData) => (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));

      // Clear error for this field when user starts typing
      if (errors[field]) {
        setErrors((prev) => ({
          ...prev,
          [field]: undefined,
        }));
      }
    };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Map form fields to store structure
      const configData = {
        github_dbt_folder_link: formData.github_dbt_folder_link,
        github_pat: formData.github_pat,
        data_limit: formData.data_limit,
      };

      // Push to store
      setDBTGithubConfigValues(configData);

      // Handle form submission logic
      console.log("DBT Form submitted:", configData);

      await streamHITLHandler(threadId, configData);
    } catch (error) {
      console.error("Error submitting form:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const abortMessageHandler = async () => {
    if (!activeRunId) return;
    const currentThread = thread || threadId;

    try {
      await chatApi.post(
        `/threads/${currentThread}/runs/${activeRunId}/cancel?wait=false&action=interrupt`
      );
    } catch (err) {
      console.log(err);
    } finally {
      // clear steps and interrupt action
      clearSteps();
      // stop Interrupt Action if available
      if (interruptAction) {
        setInterruptAction(null);
      }
      setStream(false);
      setActiveRunId(undefined);
    }
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleCancel = async () => {
    // Reset form on cancel
    setFormData({
      github_dbt_folder_link: "",
      github_pat: "",
      data_limit: 5,
    });
    setErrors({});
    setShowPassword(false);
    await abortMessageHandler();
    setInterruptAction(null);
  };

  const hasErrors = Object.keys(errors).length > 0;
  const isFormValid =
    formData.github_dbt_folder_link.trim() &&
    formData.github_pat.trim() &&
    formData.data_limit > 0 &&
    !hasErrors;

  if (isSubmitting) {
    return null;
  }

  return (
    <FormContainer>
      <StyledCard>
        <FormTitle variant="h6">DBT Configuration</FormTitle>
        <StyledCardContent>
          <FieldsContainer>
            <StyledTextField
              fullWidth
              label="GitHub DBT URL"
              variant="outlined"
              size="small"
              value={formData.github_dbt_folder_link}
              onChange={handleInputChange("github_dbt_folder_link")}
              error={!!errors.github_dbt_folder_link}
              helperText={errors.github_dbt_folder_link}
              placeholder="https://github.com/username/repository"
              hasError={!!errors.github_dbt_folder_link}
            />

            <StyledTextField
              fullWidth
              label="GitHub Personal Access Token"
              type={showPassword ? "text" : "password"}
              variant="outlined"
              size="small"
              value={formData.github_pat}
              onChange={handleInputChange("github_pat")}
              error={!!errors.github_pat}
              helperText={errors.github_pat}
              placeholder="Enter your GitHub Personal Access Token"
              hasError={!!errors.github_pat}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleTogglePasswordVisibility}
                      edge="end"
                      size="small"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <StyledTextField
              fullWidth
              label="Data Limit"
              type="number"
              variant="outlined"
              size="small"
              value={formData.data_limit}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                const value = parseInt(event.target.value) || 1;
                setFormData((prev) => ({
                  ...prev,
                  data_limit: value,
                }));

                // Clear error for this field when user starts typing
                if (errors.data_limit) {
                  setErrors((prev) => ({
                    ...prev,
                    data_limit: undefined,
                  }));
                }
              }}
              error={!!errors.data_limit}
              helperText={errors.data_limit}
              placeholder="Enter data limit (e.g., 1000)"
              hasError={!!errors.data_limit}
              inputProps={{ min: 1, max: 10000 }}
            />
          </FieldsContainer>

          <ButtonsContainer>
            <CancelButton
              variant="outlined"
              onClick={handleCancel}
              disabled={isSubmitting}
            >
              Cancel
            </CancelButton>
            <SubmitButton
              variant="contained"
              onClick={handleSubmit}
              disabled={!isFormValid || isSubmitting}
            >
              {isSubmitting ? "Submitting..." : "Submit"}
            </SubmitButton>
          </ButtonsContainer>
        </StyledCardContent>
      </StyledCard>
    </FormContainer>
  );
};

export default GithubConfigForm;
